import { Component, Vue, Watch } from 'vue-property-decorator';
import UserService from '@/services/user.service';
import LanguagePicker from '@/components/LanguagePicker/LanguagePicker.vue';
import Loader from '@/components/Loader/Loader.vue';
import Logo from '@/components/Logo/Logo.vue';
import MobileMenu from '@/components/MobileMenu/MobileMenu.vue';
import { WatchOptions } from 'vue';
import TokenService from '@/services/token.service';
import { SUPPORTED_LANGUAGES, APP_VERSION } from '@/config/app.config';
import { loadLanguageAsync } from '@/plugins/i18n';
import LocalStoreService from '@/utils/local.store';
import ModalTemplate from '@/components/ModalTemplate/ModalTemplate.vue';
import { McsisRole } from '@/models/constants/McsisRole';
import MoreInfo from '@/components/MoreInfo/MoreInfo.vue';

@Component({
  components: {
    LanguagePicker,
    Loader,
    Logo,
    MobileMenu,
    ModalTemplate,
    MoreInfo,
  },
})
export default class App extends Vue {
  // Properties
  public mobileMenuOpen: boolean = false;
  public env = process.env.NODE_ENV;
  private get suName() { return this.$route.params.screening; }
  private showUpdateModal: boolean = false;
  private showGiveUpModal: boolean = false;

  private get showEquestAtHomeContentModal(): boolean {
    return this.$store.state.showContentFromEquestAtHome;
  }

  public get isAtHome() {
    return this.$route.matched.some((record) => record.meta.atHome);
  }

  public created() {
    this.checkAppVersion();
  }

  public mounted() {
    window.addEventListener('load', () => {
      this.$store.commit('setIsAppLoaded', true);
      // run after everything is in-place
      this.setup();

      // Remove the locale from $i18n in order to force a "refresh" of the texts
      // by setting the former locale.
      const oldLocale = this.$i18n.locale;
      this.$i18n.locale = '';
      this.$i18n.locale = oldLocale;
    });
  }

  get userName() {
    return this.authenticated ? LocalStoreService.getItem(LocalStoreService.localItemKeys.proUsername) : '';
  }
  get authenticated() { return this.$store.state.isAuthenticated; }
  get isDossierOpened() { return this.$store.state.isDossierOpened; }

  get refreshTrys() {
    return parseInt(LocalStoreService.getItem(LocalStoreService.localItemKeys.appVersionTrys), 10) || 0;
  }
  set refreshTrys(value: number) {
    LocalStoreService.setItem(LocalStoreService.localItemKeys.appVersionTrys, value.toString());
  }

  get onShowGiveUpModal(): boolean {
    if (this.$store.state.giveUpClicked) {
      this.showGiveUpModal = true;
      this.$store.dispatch('setGiveUpClicked', false);
    }
    return this.showGiveUpModal;
  }

  /**
   * Check the app version when the App is first loaded
   * and force the refresh if the version is different
   * from the app-version.json file on the server.
   */
  public checkAppVersion(): void {
    fetch('/app-version.json')
      .then((res) => res.json())
      .then((data) => {
        this.$store.commit('setAppVersion', {
          isLatest: data.version === APP_VERSION,
          lastCheckDate: new Date(),
        });
        if (!this.$store.state.appVersion.isLatest) {
          if (this.refreshTrys > 1) {
            this.showUpdateModal = true;
          } else {
            this.refreshTrys++;
            window.location.reload();
          }
        } else {
          // Reset the values
          this.refreshTrys = 0;
        }
      })
      .catch((err) => {
        throw(err);
      });
  }

  public hideUpdateModal(): void {
    this.showUpdateModal = false;
  }

  public hideGiveUpModal(): void {
    this.showGiveUpModal = false;
  }

  protected hideInfoEquestAtHomeContentModal() {
    this.$store.dispatch('setShowContentFromEquestAtHome', false);
  }

  /**
   * Setup the application on first loading
   */
  public async setup() {
    TokenService.setXsrfTokenInHeader();
    
    if (TokenService.tokenExists()) {
      const isTokenValid = await TokenService.isTokenValid()
        .then((res) => {
          return res;
        })
        .catch((err) => {
          return false;
        });
      if (isTokenValid) {
        let routePrefix = this.suName;
        if (!routePrefix) {
          // Get the route prefix from the connected participant/pro user
          routePrefix = await UserService.getUserRoutePrefix();
        }
        var isEquestParticipant = await this.$isInRole(McsisRole.EquestParticipant);
        var isParticipant = await this.$isInRole(McsisRole.Participant);
        if (isEquestParticipant) {
          this.setIdentified(true).then(
            async () => {
              // redirect to login participant in order to open the participant dossier
              this.$router.push({
                  name: 'atHomeIdentity',
                  params: {
                    screening: routePrefix,
                  },
                });
            });
        } else if (!isParticipant) {
          this.setAuthenticated(true).then(
            () => {
              // redirect to login participant in order to open the participant dossier
              if (!!this.$route.name && this.$route.name !== 'participant') {
                this.$router.push({
                  name: 'participant',
                  params: {
                    screening: routePrefix,
                  },
                });
              }
            });
        }
      } else {
        // In case a token exists but is no longer valid we force a logout from the
        // frontend to be sure to clean all data
        const routePrefix = this.suName;
        UserService.logoutFromFrontend();
        if (!!this.$route.name && this.$route.name !== 'login') {
          this.$router.push({
            name: 'login',
            params: {
              screening: routePrefix,
            },
          });
        }
      }
    }
  }

  // triggered from hamburger
  protected toggleMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }
  // triggered from mobile menu links
  protected closeMobMenu(): void {
    this.mobileMenuOpen = false;
  }

  protected async setAuthenticated(status: boolean): Promise<void> {
    this.$store.dispatch('setIsAuthenticated', status);
  }

  protected async setIdentified(status: boolean): Promise<void> {
    this.$store.dispatch('setIsIdentified', status);
  }

  // Get loading state from store
  protected get isLoading(): boolean { return this.$store.state.dataLoading; }


  /**
   * Logs the participant or the user out of the app
   */
  protected async logout(): Promise<void> {
    if (this.authenticated) {
      await UserService.logout();
    }
  }

  /**
   * Go to the confirmation when the participant gives up.
   */
  protected giveUp(): void {
    this.hideGiveUpModal();
    this.$store.dispatch('setHasGivenUp', true);
    this.$router.push({
      name: 'confirmation',
      params: {
        screening: this.$route.params.screening,
      },
    });
  }

  protected addPopupMessageBeforeUnload(): void {
    if (window.onbeforeunload) {
      return;
    }

    window.onbeforeunload = (ev: any) => {
      const e = ev || window.event;
      const warningText = this.$i18n.t('msgs.notSavedDataWarningMessage').toString();

      // For IE and Firefox
      if (e) {
        e.returnValue = warningText;
      }

      // For Safari, Chrome
      return warningText;
    };
  }

  protected removePopupMessageBeforeUnload(): void {
    if (window.onbeforeunload) {
      window.onbeforeunload = null;
    }
  }

  @Watch('$route.name', { immediate: true, deep: true } as WatchOptions)
  protected onRouteNameChanged(to: string, from: string) {
    if (to !== undefined) {
      if (to !== 'confirmation') {
        this.addPopupMessageBeforeUnload();
      } else {
        this.removePopupMessageBeforeUnload();
      }
    } else {
      this.removePopupMessageBeforeUnload();
    }
  }

  @Watch('suName', { immediate: true, deep: true } as WatchOptions)
  protected onLocaleChanged(suName: string, oldSuName: string) {
    if (oldSuName !== suName && suName !== undefined) {
      this.$store.dispatch('setIsLanguagesLoaded', false);
      // Load languages by SU
      SUPPORTED_LANGUAGES.forEach((lang: string) => {
        loadLanguageAsync(lang, suName).then(() => {
          this.$store.dispatch('setIsLanguagesLoaded', true);
        });
      });
    }
  }
}
